/**
 * Catálogo de tutoriales en video de Facil Control.
 *
 * Fuente única de verdad — no hardcodear IDs, títulos ni URLs de YouTube en
 * componentes. La lista se mantiene a mano; cuando se integre la API de YouTube
 * para listar el canal automáticamente, esta constante se reemplaza por el
 * resultado de esa consulta y la página de tutoriales no necesita cambiar.
 *
 * Los títulos de acá NO son los del canal de YouTube: varios videos están
 * publicados como "Tutorial 6", "Tutorial 7"… El título que ve el usuario tiene
 * que decir qué resuelve el video, así que se escribe con el vocabulario del
 * producto (el mismo del menú y de los encabezados de pantalla).
 */

/**
 * Plataforma en la que transcurre el video.
 *
 * Agrupa la página, y no es cosmético: quien busca "cómo firmo una entrega de
 * EPP" está en terreno con el teléfono, y quien busca "cómo asigno licencias"
 * está sentado frente al panel. Mezclarlos obliga a leer los ocho títulos para
 * descartar los que no le sirven.
 */
export type PlataformaTutorial = 'app' | 'web'

export interface Tutorial {
  /** ID del video de YouTube (lo que va después de `v=` en la URL). */
  id: string
  /** Título visible en la tarjeta, en vocabulario del producto. */
  titulo: string
  /** Duración en segundos. Se muestra para que el usuario sepa cuánto dura. */
  duracion: number
  plataforma: PlataformaTutorial
}

export const TUTORIALES: Tutorial[] = [
  // --- app móvil: lo que se firma en terreno ------------------------------
  {
    id: 'jt3ikL6SS2o',
    titulo: 'Cómo hacer un Check Vehículos',
    duracion: 121,
    plataforma: 'app',
  },
  {
    id: 'oMKIEttLoU4',
    titulo: 'Cómo hacer un análisis de trabajo seguro (ATS)',
    duracion: 175,
    plataforma: 'app',
  },
  {
    id: 'LadGKteZyLg',
    titulo: 'Cómo generar y firmar una entrega de EPP',
    duracion: 83,
    plataforma: 'app',
  },
  {
    id: 'PmZZGXAk2Hw',
    titulo: 'Cómo crear una nueva lista de capacitación',
    duracion: 129,
    plataforma: 'app',
  },

  // --- panel web: configuración y seguimiento -----------------------------
  {
    id: 'e9fnxKBLBS4',
    titulo: 'Cómo cargar la documentación de un trabajador',
    duracion: 216,
    plataforma: 'web',
  },
  {
    id: 'TQr052RU7Hg',
    titulo: 'Cómo crear elementos de EPP y cargar su certificado',
    duracion: 263,
    plataforma: 'web',
  },
  {
    id: '7IULso7Ixjs',
    titulo: 'Cómo asignar licencias a los usuarios',
    duracion: 162,
    plataforma: 'web',
  },
  {
    id: 'tM5ifJ4arSE',
    titulo: 'Cómo revisar y descargar los documentos firmados',
    duracion: 459,
    plataforma: 'web',
  },
]

/** Grupos de la página, en orden de aparición. */
export const GRUPOS_TUTORIALES: {
  plataforma: PlataformaTutorial
  titulo: string
  descripcion: string
}[] = [
  {
    plataforma: 'app',
    titulo: 'En la app móvil',
    descripcion: 'Lo que se hace en terreno, desde el teléfono.',
  },
  {
    plataforma: 'web',
    titulo: 'En el panel web',
    descripcion: 'Lo que se configura y se revisa desde el computador.',
  },
]

/** Ruta interna de la página de tutoriales en video. */
export const TUTORIALES_ROUTE = '/tutoriales'

/** `216` → `"3:36"`. */
export function formatearDuracion(segundos: number): string {
  const minutos = Math.floor(segundos / 60)
  const resto = segundos % 60
  return `${minutos}:${String(resto).padStart(2, '0')}`
}

/**
 * Miniatura del video.
 *
 * `maxres` es 1280×720 y no existe para todos los videos; el llamador cae a
 * `hq` (que siempre existe) si falla la carga. `mq` es 320×180 y va en la
 * lista, donde la miniatura mide menos de cien píxeles de ancho: bajar la de
 * 1280 para eso es tirar ocho imágenes grandes a la basura.
 */
export function urlMiniatura(
  id: string,
  calidad: 'maxres' | 'hq' | 'mq' = 'maxres',
): string {
  return `https://i.ytimg.com/vi/${id}/${calidad}default.jpg`
}

/**
 * URL del reproductor embebido.
 *
 * Va contra `youtube-nocookie.com`: no deja cookies de seguimiento hasta que el
 * usuario reproduce. El iframe recién se monta al hacer clic (ver la página),
 * así que `autoplay` va siempre en 1 — sin él habría que apretar play dos veces.
 */
export function urlEmbed(id: string): string {
  return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`
}
